import LandingSection from '@/components/landing/LandingSection'
import JsonLd from '@/components/JsonLd'
import { getHowToSchema, getBreadcrumbSchema } from '@/utils/schemas'
import Link from 'next/link'

const howToSteps = [
  {
    name: 'Paste a YouTube link',
    text: 'Copy a YouTube video URL and paste it into Annotate.tv. The video will load in the annotation interface with the transcript ready to view.',
  },
  {
    name: 'Take timestamped notes',
    text: 'As you watch the video, add notes at any point. Each note automatically saves the timestamp, so you can click it later to jump back to that exact moment.',
  },
  {
    name: 'Use AI to chat with the video',
    text: 'Ask questions about the video content and get instant answers from the AI based on the transcript. Find specific information without rewinding.',
  },
  {
    name: 'Extract text from slides',
    text: 'When the video shows a slide or text on screen, use the OCR feature to extract that text directly into your notes with one click.',
  },
  {
    name: 'Export your notes',
    text: 'Export your annotated notes to PDF for printing, Markdown for Notion/Evernote/Obsidian, or sync directly to Readwise for spaced repetition review.',
  },
]

const howToSchema = getHowToSchema({
  name: 'How to take notes on YouTube videos with Annotate.tv',
  description:
    'Learn how to use Annotate.tv to take timestamped notes on YouTube videos, chat with videos using AI, extract text from slides, and export your notes.',
  steps: howToSteps,
})

const breadcrumbs = [
  { name: 'Home', path: '/' },
  { name: 'How It Works', path: '/how-it-works' },
]

export default function HowItWorks() {
  return (
    <LandingSection
      title="How It Works"
      description="Learn how to use Annotate.tv to take timestamped notes on YouTube videos, chat with videos using AI, and export your notes."
      canonical="/how-it-works"
    >
      <JsonLd data={[howToSchema, getBreadcrumbSchema(breadcrumbs)]} />

      {/* Hero */}
      <div className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl">
            How Annotate.tv Works
          </h1>
          <p className="mt-4 text-xl text-gray-600">
            Take timestamped notes on YouTube videos in 5 simple steps
          </p>
        </div>
      </div>

      {/* Steps */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {howToSteps.map((step, index) => (
              <Step
                key={index}
                number={index + 1}
                title={step.name}
                description={step.text}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Features breakdown */}
      <div className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Key Features Explained
          </h2>

          <div className="space-y-12">
            <FeatureSection
              title="Timestamped Notes"
              description="Every note you take is linked to the exact moment in the video. Click any note to jump directly to that timestamp. This makes reviewing videos efficient - no more scrubbing through hours of content to find what you need."
            />

            <FeatureSection
              title="AI Chat with Video"
              description="Ask questions like 'What were the main points about marketing?' and get answers based on the video's transcript. The AI reads the entire transcript and provides relevant answers with context. Available with Premium."
            />

            <FeatureSection
              title="Text Extraction (OCR)"
              description="When a video shows slides, code, or any text on screen, click the camera button to extract that text automatically. The OCR feature reads the current frame and adds the text to your notes. No more pausing and typing out slides."
            />

            <FeatureSection
              title="Export Options"
              description="Export your annotated notes to PDF for printing or offline reading. Use Markdown export for Notion, Evernote, or Obsidian. Premium users can sync directly with Readwise for spaced repetition review of video highlights."
            />

            <FeatureSection
              title="Video Transcript"
              description="View the full transcript alongside the video. Click any line in the transcript to jump to that moment. Search within the transcript to find specific topics quickly."
            />

            <FeatureSection
              title="Organization"
              description="Group videos into playlists to organize by topic, course, or project. Search across all your notes and videos to find information instantly."
            />
          </div>
        </div>
      </div>

      {/* Getting started */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
            Getting Started
          </h2>

          <div className="bg-white rounded-lg p-8 shadow-sm">
            <ol className="space-y-4 text-gray-600">
              <li className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-primary text-white text-sm font-medium flex items-center justify-center mr-3">
                  1
                </span>
                <span>
                  <strong className="text-gray-900">
                    Create a free account
                  </strong>{' '}
                  - Sign up with Google or email. No credit card required.
                </span>
              </li>
              <li className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-primary text-white text-sm font-medium flex items-center justify-center mr-3">
                  2
                </span>
                <span>
                  <strong className="text-gray-900">
                    Add your first video
                  </strong>{' '}
                  - Paste any YouTube URL to start annotating.
                </span>
              </li>
              <li className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-primary text-white text-sm font-medium flex items-center justify-center mr-3">
                  3
                </span>
                <span>
                  <strong className="text-gray-900">Start taking notes</strong>{' '}
                  - The free plan includes 5 videos with unlimited notes.
                </span>
              </li>
              <li className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-primary text-white text-sm font-medium flex items-center justify-center mr-3">
                  4
                </span>
                <span>
                  <strong className="text-gray-900">
                    Try Premium free for 14 days
                  </strong>{' '}
                  - Unlock AI chat, OCR, and all export options with no
                  commitment.
                </span>
              </li>
            </ol>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Ready to try it?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Start taking notes on YouTube videos in seconds
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/signup">
              <a className="inline-block bg-primary text-white px-8 py-4 rounded-lg text-lg font-medium clickable-opacity">
                Start Free
              </a>
            </Link>
            <Link href="/">
              <a className="inline-block bg-gray-100 text-gray-700 px-8 py-4 rounded-lg text-lg font-medium clickable-opacity">
                Try Demo First
              </a>
            </Link>
          </div>
        </div>
      </div>
    </LandingSection>
  )
}

function Step({ number, title, description }) {
  return (
    <div className="flex items-start">
      <div className="flex-shrink-0">
        <div className="h-12 w-12 rounded-full bg-primary text-white text-xl font-bold flex items-center justify-center">
          {number}
        </div>
      </div>
      <div className="ml-6">
        <h3 className="text-xl font-bold text-gray-900">{title}</h3>
        <p className="mt-2 text-gray-600">{description}</p>
      </div>
    </div>
  )
}

function FeatureSection({ title, description }) {
  return (
    <div className="border-b border-gray-200 pb-8 last:border-0">
      <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
      <p className="text-gray-600 leading-relaxed">{description}</p>
    </div>
  )
}
